<template>
  <div class="article-container" :class="night ? 'night' : null">
    <van-skeleton :row="2" :loading="loading"></van-skeleton>
    <van-skeleton title :row="5" :loading="loading"></van-skeleton>
    <van-skeleton :row="5" :loading="loading">
      <div v-if="article.contentType === '5'">
        <Pictures :article="article"></Pictures>
      </div>
      <Empty v-else-if="!article.content"></Empty>
      <div v-else class="article" :class="contentClass">
        <Title :propsData="article"></Title>
        <AttributeBar :propsData="article" @translate="translate"></AttributeBar>
        <template v-if="!isAiSummaryEnabled">
          <div v-if="hasTag" class="tag-bar flex-wrap">
            <div class="tag-bar-tag" v-for="tag in article.tagInfo.slice(0, 3)" :key="tag.name">{{ tag.name }}</div>
          </div>
          <div v-if="article.contentType === '2'" class="article-content">
            <video width="100%" height="500" controls="controls" :poster="article.videoInfo.videoPicUrl || null">
              <source :src="article.videoInfo.videoUrl" type="video/mp4">
            </video>
          </div>
          <template v-else>
            <div class="article-content" v-dompurify-html="article.content" @click="onContentClick"></div>
          </template>
        </template>
        <template v-else>
          <div v-if="aiActionButtons.length" class="ai-reading-panel">
            <div class="ai-entry-title">AI资讯解读</div>
            <div class="ai-action-grid">
              <button
                v-for="action in aiActionButtons"
                :key="action.key"
                type="button"
                class="ai-action-button"
                @click="openAiPopup(action)"
              >
                <i class="ai-action-icon" :class="action.icon"></i>
                <span class="ai-action-title">{{ action.title }}</span>
              </button>
            </div>
          </div>
          <div v-if="hasTag" class="tag-bar flex-wrap">
            <div class="tag-bar-tag" v-for="tag in article.tagInfo.slice(0, 3)" :key="tag.name">{{ tag.name }}</div>
          </div>
          <div v-if="article.contentType === '2'" class="article-content">
            <video width="100%" height="500" controls="controls" :poster="article.videoInfo.videoPicUrl || null">
              <source :src="article.videoInfo.videoUrl" type="video/mp4">
            </video>
          </div>
          <template v-else>
            <div class="article-content" v-dompurify-html="article.content" @click="onContentClick"></div>
          </template>
        </template>
        <van-popup v-model="showAiPopup" position="bottom" round class="ai-content-popup">
          <div class="ai-popup-panel">
            <div class="ai-popup-handle"></div>
            <div class="ai-popup-header">
              <div class="ai-popup-heading">
                <img class="ai-reading-logo" src="@/assets/images/ai-tabs/ai-logo.svg" alt="">
                <span class="ai-popup-title">AI资讯解读</span>
                <span class="ai-reading-badge">AI生成</span>
              </div>
              <button type="button" class="ai-popup-close" aria-label="关闭" @click="closeAiPopup"></button>
            </div>
            <div class="ai-popup-tabs">
              <button
                v-for="action in aiActionButtons"
                :key="action.key"
                type="button"
                class="ai-popup-tab"
                :class="{ active: action.key === activeAiModuleKey }"
                @click="switchAiPopupTab(action)"
              >
                <i class="ai-popup-tab-icon" :class="action.icon"></i>
                <span class="ai-popup-tab-title">{{ action.title }}</span>
              </button>
            </div>
            <div ref="aiPopupContent" class="ai-popup-content">
              <AiSummary :module="activeAiPopupModule"></AiSummary>
            </div>
          </div>
        </van-popup>
      </div>
      <div class="article-disclaimer">
        {{ disclaimer }}
      </div>
    </van-skeleton>
  </div>
</template>
<script>
import {
  removeTags,
  linkStocks,
  removeWhiteSpaceInTags,
  removeTableStyle,
  disclaimer,
} from '@/views/detailPage/utils/article';
import Cookies from 'js-cookie';
import { detailPage, deepInsight, eventTrack } from '@/api/b2b-news';
import { generateRandomCode, translateStock } from '@/views/detailPage/utils/util';
import Title from '@/views/detailPage/components/Title';
import AttributeBar from '@/views/detailPage/components/AttributeBar';
import AiSummary from '@/views/detailPage/components/AiSummary';
import Empty from '@/views/detailPage/components/Empty';
import Pictures from './components/Pictures';
import { getQsid, getQX } from '@/views/detailPage/utils/qsid';
import { openStock, isSupportStockLink } from '@/plugins/native';
import mixinWxShare from '@/mixins/mixin-wx-share';
import { Skeleton, ImagePreview as imagePreview, Popup } from 'vant';

const AI_ACTION_CONFIGS = [
  {
    key: 'summary',
    type: 'summary',
    title: '看要点',
    icon: 'tab-icon-summary',
  },
  {
    key: 'deepInsight',
    type: 'deepInsight',
    title: '深度解读',
    icon: 'tab-icon-insight',
  },
];

export default {
  data() {
    return {
      loading: false,
      accessKey: '',
      lang: '',
      article: {
        title: '',
      },
      activeAiModuleKey: '',
      showAiPopup: false,
      disclaimer,
      beginTime: 0,
    };
  },
  mixins: [mixinWxShare],
  computed: {
    night() {
      return window.navigator.userAgent.indexOf('hxtheme/1') > -1 || this.$route.query?.theme === 'black';
    },
    hasTag() {
      return this.article.tagInfo && this.article.tagInfo.length;
    },
    isAiSummaryEnabled() {
      return Boolean(getQX('aiSummary'));
    },
    aiModuleMap() {
      return (this.article.aiModules || []).reduce((map, module) => {
        map[module.key] = module;
        return map;
      }, {});
    },
    aiActionButtons() {
      return AI_ACTION_CONFIGS
        .map(config => this.aiModuleMap[config.key])
        .filter(item => item && this.hasModuleContent(item.content));
    },
    activeAiPopupModule() {
      return this.aiActionButtons.find(item => item.key === this.activeAiModuleKey) || {};
    },
    articleFontSize: {
      get() {
        return this.$store.state.articleFontSize;
      },
      set(value) {
        this.$store.commit('SET_ARTICLE_FONT_SIZE', value);
      },
    },
    contentClass() {
      return {
        [`article-size-${this.articleFontSize}`]: true,
        'break-word': this.lang === 'en',
      };
    },
  },
  components: {
    [Title.name]: Title,
    [AttributeBar.name]: AttributeBar,
    [AiSummary.name]: AiSummary,
    [Popup.name]: Popup,
    [Skeleton.name]: Skeleton,
    [Empty.name]: Empty,
    [Pictures.name]: Pictures,
  },
  mounted() {
    this.accessKey = this.$route.query?.accessKey || process.env.VUE_APP_ACCESS_KEY;
    this.initLang();
    this.uploadWeblog();
    this.getDetail();
  },
  methods: {
    initLang() {
      const lang = this.$route.query.lang?.toLowerCase();
      if (lang) {
        // 赋值+兼容
        this.lang = lang === 'tw' ? 't' : lang;
      }
    },
    changeDisclaimer(str) {
      if (str) {
        this.disclaimer = str;
      }
    },
    uploadWeblog() {
      const beforeUnloadWeblog = () => {
        if (this.beginTime) {
          window.weblog?.report({
            id: 'b2bInfo_b2b_pubInfoDetail',
            action: 'stay',
            logmap: {
              stayTime: new Date().getTime() - this.beginTime,
              pid: this.$route.params.id,
              content: this.accessKey,
              source: this.article.source,
              suid: localStorage.zxUserId,
            }
          });
          eventTrack({
            accesskey: this.accessKey,
            pageName: 'PUBLIC_DETAIL',
            userId: localStorage.zxUserId,
            template: '阅读时长',
            eventType: 'PAGE_STAY',
            properties: {
              access_key: this.accessKey,
              read_duration: new Date().getTime() - this.beginTime,
              content_id: this.article.contentId,
              column_code: this.article.columnCode?.join(','),
              platform_code: this.article.platformEn
            }
          });
        }
        window.removeEventListener('beforeunload', beforeUnloadWeblog);
      };
      window.addEventListener('beforeunload', beforeUnloadWeblog);

      this.beginTime = new Date().getTime();
      if (!localStorage.zxUserId) {
        const num_4 = 4;
        localStorage.zxUserId = `${this.accessKey + this.beginTime + generateRandomCode(num_4)}`;
      }
      Cookies.set('userid', localStorage.zxUserId);
    },
    getDetail() {
      const {id} = this.$route.params;
      const from = this.$route.query.from?.toLowerCase();
      const usid = getQsid();
      const params = {
        ...this.lang ? { lang: this.lang } : {},
        ...usid ? { u: usid } : {}
      };
      if (from && from.indexOf('c') === 0) {
        params.infoId = id;
      } else {
        params.contentId = id;
      }
      this.detailPageData(params);
    },
    detailPageData(params) {
      this.loading = true;
      detailPage(params).then(res => {
        this.loading = false;
        const { data, code, disclaimer: qsDisclaimer } = res;
        if (code === 0) {
          let content = removeTags(data.content);
          content = linkStocks(content, data.stocks);
          this.article = {
            ...data,
            content: removeTableStyle(removeWhiteSpaceInTags(content)),
            tagInfo: data.tagInfo ? data.tagInfo.slice(0, this.$num.num_3) : [],
          };
          this.activeAiModuleKey = '';
          this.showAiPopup = false;
          this.changeTitle(data.title);
          this.changeDisclaimer(qsDisclaimer);
          window.weblog?.report({
            id: 'b2bInfo_b2b_pubInfoDetail',
            action: 'show',
            logmap: {
              pid: this.$route.params.id,
              content: this.accessKey,
              source: this.article.source,
              suid: localStorage.zxUserId,
            }
          });
          try {
            eventTrack({
              accesskey: this.accessKey,
              pageName: 'PUBLIC_DETAIL',
              userId: localStorage.zxUserId,
              template: '阅读量',
              eventType: 'LOAD',
              properties: {
                access_key: this.accessKey,
                content_id: this.article.contentId,
                column_code: this.article.columnCode?.join(','),
                platform_code: this.article.platformEn
              }
            });
          } catch (error) {
            console.log('埋点异常', error);
          }
          this.getWxShareInfoData({
            link: window.location.href,
            title: this.article.title,
            desc: this.article.isAbstract || ' '
          });
          this.getAiSummary(data);
        }
      });
    },
    getAiSummary(data) {
      const accessKey = getQX('aiSummary');
      if (!this.isAiSummaryEnabled || !accessKey || data.platformEn !== 'THS' || !data.itemId) {
        return;
      }
      deepInsight({
        seq: data.itemId,
        accessKey,
      }).then(res => {
        const aiModules = this.formatAiModules(res?.data);
        if (aiModules.length) {
          const summaryModule = aiModules.find(item => item.type === 'summary');
          this.$set(this.article, 'aiModules', aiModules);
          if (summaryModule?.content) {
            this.$set(this.article, 'aiSummary', summaryModule.content);
          }
        }
      }).catch(() => null);
    },
    formatAiModules(data = {}) {
      const modules = [];
      const summaryContents = this.getSummaryContents(data.aiSummaries);
      const pushModule = (key, module = {}, extra = {}) => {
        const config = AI_ACTION_CONFIGS.find(item => item.key === key);
        const content = module?.content || '';
        if (!config || !this.hasModuleContent(content)) {
          return;
        }
        modules.push({
          ...config,
          ...module,
          ...extra,
          content,
        });
      };

      if (summaryContents.length) {
        pushModule('summary', {
          content: summaryContents.join('\n'),
        });
      }
      if (this.hasModuleContent(data.deepInsight?.content)) {
        pushModule('deepInsight', data.deepInsight, { isHtml: true });
      }

      return modules;
    },
    hasModuleContent(content) {
      return typeof content === 'string' ? Boolean(content.trim()) : Boolean(content);
    },
    getSummaryContents(aiSummaries) {
      if (!Array.isArray(aiSummaries)) {
        return [];
      }
      return aiSummaries
        .map(item => typeof item === 'string' ? item : item?.content || item?.summary || item?.text)
        .filter(content => typeof content === 'string' ? Boolean(content.trim()) : Boolean(content));
    },
    changeTitle(title) {
      window.document.title = title || '资讯';
    },
    translate(lang) {
      this.lang = lang;
      this.getDetail();
    },
    onContentClick(e) {
      if (e.target.nodeName === 'IMG') {
        imagePreview([e.target.currentSrc]);
      }
      if (e.target.classList.contains('link-stock') && isSupportStockLink()) {
        const code = translateStock(e.target.dataset.code, e.target.dataset.market);
        const name = e.target.innerText;
        const {market} = e.target.dataset;
        openStock(code, name, market);
      }
    },
    openAiPopup(action) {
      if (!action.content) {
        return;
      }
      this.activeAiModuleKey = action.key;
      this.showAiPopup = true;
      this.resetAiPopupScroll();
    },
    switchAiPopupTab(action) {
      if (!action.content || action.key === this.activeAiModuleKey) {
        return;
      }
      this.activeAiModuleKey = action.key;
      this.resetAiPopupScroll();
    },
    resetAiPopupScroll() {
      this.$nextTick(() => {
        const content = this.$refs.aiPopupContent;
        if (content) {
          content.scrollTop = 0;
        }
      });
    },
    closeAiPopup() {
      this.showAiPopup = false;
    }
  }
};
</script>
<style lang="less">
@import url('../style/common.less');

.van-skeleton {
  padding-top: 20px;
}

.night {

  .van-skeleton__row,
  .van-skeleton__title {
    background-color: #A3A3A3
  }
}

.ai-reading-panel {
  position: relative;
  margin: 0 15px 16px;
  padding: 17px 8px 9px;
  border: 1px solid #D6E6F6;
  border-radius: 12px;
  background: #EDF6FF;
}

.ai-entry-title {
  position: absolute;
  top: -9px;
  left: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: 18px;
  padding: 1px 8px;
  border: 1px solid #B9D9F6;
  border-radius: 999px;
  background: #EDF6FF;
  color: #168BFA;
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  white-space: nowrap;
  transform: translateX(-50%);
}

.ai-reading-logo {
  display: block;
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  margin-right: 6px;
}

.ai-reading-badge {
  flex: 0 0 auto;
  height: 14px;
  margin-left: 8px;
  padding: 0 5px;
  border-radius: 7px;
  background: #EEF6FF;
  color: #188BFA;
  font-size: 11px;
  line-height: 14px;
}

.ai-action-grid {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  min-height: 30px;
}

.ai-action-button {
  appearance: none;
  -webkit-appearance: none;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex: 0 0 auto;
  width: auto;
  min-height: 30px;
  padding: 4px 10px;
  border: 1px solid #E3F0FF;
  border-radius: 12px;
  background: #FCFDFF;
  color: #4E5969;
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  outline: 0;
  white-space: nowrap;
}

.ai-action-icon {
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
  margin-right: 4px;
  background-color: currentColor;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 14px 14px;
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: 14px 14px;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: 14px 14px;
}

.tab-icon-summary {
  mask-image: url('~@/assets/images/ai-tabs/tab-summary.svg');
  -webkit-mask-image: url('~@/assets/images/ai-tabs/tab-summary.svg');
}

.tab-icon-insight {
  mask-image: url('~@/assets/images/ai-tabs/tab-insight.svg');
  -webkit-mask-image: url('~@/assets/images/ai-tabs/tab-insight.svg');
}

.ai-action-title {
  flex: 0 0 auto;
  white-space: nowrap;
}

.ai-content-popup {
  max-height: 72vh;
  overflow: hidden;
  background: #FFFFFF;
}

.ai-popup-panel {
  display: flex;
  flex-direction: column;
  max-height: 72vh;
}

.ai-popup-handle {
  flex: 0 0 auto;
  width: 36px;
  height: 4px;
  margin: 6px auto 8px;
  border-radius: 2px;
  background: #E5E6EB;
}

.ai-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  height: 40px;
  padding: 0 14px;
}

.ai-popup-heading {
  display: flex;
  align-items: center;
  height: 24px;
  min-width: 0;
}

.ai-popup-title {
  min-width: 0;
  color: #1F2329;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
}

.ai-popup-tabs {
  display: flex;
  flex: 0 0 auto;
  height: 44px;
  padding: 0 15px;
  overflow-x: auto;
  border-top: 1px solid #F2F3F5;
  border-bottom: 1px solid #F2F3F5;
  background: #FFFFFF;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
}

.ai-popup-tab {
  appearance: none;
  -webkit-appearance: none;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex: 1 0 0;
  min-width: 96px;
  height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #4E5969;
  font-family: inherit;
  font-size: 16px;
  font-weight: 400;
  line-height: 44px;
  outline: 0;
}

.ai-popup-tab.active {
  color: #188BFA;
  font-weight: 600;
}

.ai-popup-tab.active::after {
  position: absolute;
  right: 50%;
  bottom: 0;
  width: 16px;
  height: 3px;
  border-radius: 2px;
  background: #188BFA;
  content: '';
  transform: translateX(50%);
}

.ai-popup-tab-icon {
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
  margin-right: 4px;
  background-color: currentColor;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 14px 14px;
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: 14px 14px;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: 14px 14px;
}

.ai-popup-tab-title {
  flex: 0 0 auto;
  white-space: nowrap;
}

.ai-popup-close {
  position: relative;
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  margin-left: 12px;
  padding: 0;
  border: 0;
  background: transparent;
}

.ai-popup-close::before,
.ai-popup-close::after {
  position: absolute;
  top: 11px;
  left: 5px;
  width: 14px;
  height: 1px;
  border-radius: 1px;
  background: #999999;
  content: '';
}

.ai-popup-close::before {
  transform: rotate(45deg);
}

.ai-popup-close::after {
  transform: rotate(-45deg);
}

.ai-popup-content {
  flex: 1 1 auto;
  min-height: 0;
  padding: 14px 0 18px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.night .ai-reading-panel {
  border-color: #1E4558;
  background: #131929;
}

.night .ai-entry-title {
  color: #5AA3E8;
  border-color: #1E4558;
  background: #131929;
}

.night .ai-reading-badge {
  background: rgba(24, 139, 250, 0.12);
}

.night .ai-action-button {
  border-color: #1E4558;
  background: #131D33;
  color: #6B7A99;
}

.night .ai-popup-tabs {
  border-color: #494949;
  background: @dark-color-bg-page;
}

.night .ai-popup-tab {
  color: #A3A3A3;
}

.night .ai-popup-tab.active {
  color: #188BFA;
}

.night .ai-content-popup {
  background: @dark-color-bg-page;
}

.night .ai-popup-handle {
  background: #494949;
}

.night .ai-popup-title {
  color: @dark-color-text-primary;
}

.night .ai-popup-close::before,
.night .ai-popup-close::after {
  background: @dark-color-text-gray;
}
</style>
